import React, { useState, useMemo } from 'react';
import { getIn } from 'formik';
import { parseISO } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

import DatePicker from 'components/DatePicker/DatePicker';
import AutocompleteDropdown from 'components/AutocompleteDropdown/AutocompleteDropdown';
import {
  categoryOrder,
  getOperatorById,
  getOperatorByTypeAndOp,
  getTypeAndOp,
  getFilteredOperatorsByCategory,
  detectValueType,
} from '../UnifiedOperators/UnifiedOperators';
import { useGetOrderFieldSchemasQuery } from 'modules/audiences/audienceApi';

const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

const getValueTypeForKey = (key, fallbackType = 'str') => {
  if (key && key.includes('.')) {
    return 'obj';
  }
  return fallbackType;
};

const RuleRow = ({
  rule,
  ruleIndex,
  setIndex,
  group,
  fieldName,
  form,
  onRemove,
  canRemove,
  isReadOnly,
  isSubmitting,
}) => {
  const [operatorMenuOpen, setOperatorMenuOpen] = useState(false);
  const [operatorSearch, setOperatorSearch] = useState('');
  const [holdFocus, setHoldFocus] = useState(false);

  const { data: orderFieldSchemas = [] } = useGetOrderFieldSchemasQuery();

  // Get validation errors for this rule
  const errors = getIn(form.errors, fieldName);
  const keyError = errors?.key;
  const valueError = errors?.value;
  const showErrors = form.submitCount > 0;

  const currentOperator = useMemo(() => {
    if (rule._operatorId) {
      return getOperatorById(rule._operatorId);
    }
    if (!rule.value_type || !rule.kv_relational_op) {
      return getOperatorById('equals');
    }
    return getOperatorByTypeAndOp(rule.value_type, rule.kv_relational_op);
  }, [rule._operatorId, rule.value_type, rule.kv_relational_op]);

  const resetOperatorMenu = () => {
    setOperatorSearch('');
    setOperatorMenuOpen(false);
    setHoldFocus(false);
  };

  // Handle custom key change
  const handleKeyChange = (newKey) => {
    const newType = getValueTypeForKey(newKey, 'str');

    if (newType !== rule.value_type) {
      form.setFieldValue(fieldName, {
        ...rule,
        key: newKey,
        value_type: newType,
      });
    } else {
      form.setFieldValue(`${fieldName}.key`, newKey);
    }
  };

  // Handle operator selection
  const selectOperator = (operator) => {
    const { value_type: detectedType, kv_relational_op: newOp } = getTypeAndOp(
      operator,
      rule.value
    );

    form.setFieldValue(fieldName, {
      ...rule,
      value_type: getValueTypeForKey(rule.key, detectedType),
      kv_relational_op: newOp,
      _operatorId: operator.id,
    });
    resetOperatorMenu();
  };

  // Handle value change
  const handleValueChange = (e) => {
    const newValue = e.target.value;

    if (currentOperator?.numericOnly) {
      const detectedType = detectValueType(newValue);
      const newType = getValueTypeForKey(rule.key, detectedType);

      if (newType !== rule.value_type && (newType === 'obj' || newType === 'int' || newType === 'dec')) {
        form.setFieldValue(fieldName, {
          ...rule,
          value: newValue,
          value_type: newType,
        });
        return;
      }
    }

    form.setFieldValue(`${fieldName}.value`, newValue);
  };

  // Handle date change
  const handleDateChange = (dt) => {
    if (!dt || !(dt instanceof Date) || isNaN(dt.getTime())) {
      form.setFieldValue(`${fieldName}.value`, '');
      return;
    }
    const utcDate = zonedTimeToUtc(dt, userTz);
    form.setFieldValue(`${fieldName}.value`, utcDate.toISOString().split('T')[0]);
  };

  const filteredOperatorsByCategory = getFilteredOperatorsByCategory(operatorSearch);
  const hasOperatorResults = Object.keys(filteredOperatorsByCategory).length > 0;

  // Parse date value for date picker
  let selectedDate = null;
  if (rule.value && currentOperator?.inputType === 'date') {
    try {
      if (typeof rule.value === 'string' && rule.value.match(/^\d{4}-\d{2}-\d{2}/)) {
        selectedDate = parseISO(rule.value);
      }
    } catch (err) {
      selectedDate = null;
    }
  }

  return (
    <div className="aud-v2-rule">
      {/* Key/Parameter Field */}
      <div className={`aud-v2-rule__field aud-v2-rule__field--key ${showErrors && keyError ? 'aud-v2-rule__field--error' : ''}`}>
        <AutocompleteDropdown
          value={rule.key !== 'custom_key' ? rule.key : ''}
          onChange={handleKeyChange}
          suggestions={orderFieldSchemas}
          placeholder="Data key: i.e. customer.email"
          disabled={isReadOnly || isSubmitting}
        />
        {showErrors && keyError && (
          <div className="aud-v2-rule__error">{keyError}</div>
        )}
      </div>

      {/* Operator Field */}
      <div className="aud-v2-rule__field aud-v2-rule__field--op">
        <div className="aud-v2-op-select">
          <input
            type="text"
            className="aud-v2-select aud-v2-select--searchable"
            autoComplete="off"
            placeholder={currentOperator?.label || 'Select operator'}
            value={operatorSearch}
            disabled={isReadOnly || isSubmitting}
            onChange={(e) => setOperatorSearch(e.target.value)}
            onFocus={() => setOperatorMenuOpen(true)}
            onBlur={() => !holdFocus && resetOperatorMenu()}
          />
          <span className="aud-v2-op-select__arrow material-icons">
            expand_more
          </span>

          {operatorMenuOpen && (
            <div className="aud-v2-op-menu">
              {hasOperatorResults ? (
                categoryOrder.map((category) => {
                  const operators = filteredOperatorsByCategory[category];
                  if (!operators || operators.length === 0) return null;

                  return (
                    <div key={category} className="aud-v2-op-menu__group">
                      <div className="aud-v2-op-menu__category">{category}</div>
                      {operators.map((op) => (
                        <div
                          key={op.id}
                          className={`aud-v2-op-menu__item ${
                            currentOperator?.id === op.id ? 'aud-v2-op-menu__item--active' : ''
                          }`}
                          onMouseDown={() => setHoldFocus(true)}
                          onClick={() => selectOperator(op)}
                        >
                          {op.label}
                          {op.hint && (
                            <span className="aud-v2-op-menu__hint">{op.hint}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })
              ) : (
                <div className="aud-v2-op-menu__empty">No matching operators</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Value Field */}
      <div className="aud-v2-rule__field aud-v2-rule__field--value">
        {currentOperator?.inputType === 'date' ? (
          <DatePicker
            inputStyle={{ width: '100%' }}
            selectedDate={selectedDate}
            onInputChange={handleDateChange}
          />
        ) : (
          <input
            type={currentOperator?.inputType === 'number' ? 'number' : 'text'}
            className={`aud-v2-input ${showErrors && valueError ? 'aud-v2-input--error' : ''}`}
            placeholder={currentOperator?.placeholder || 'Enter value...'}
            value={rule.value || ''}
            onChange={handleValueChange}
            disabled={isReadOnly || isSubmitting}
          />
        )}
        {showErrors && valueError && (
          <div className="aud-v2-rule__error">{valueError}</div>
        )}
      </div>

      {/* Remove Button */}
      {canRemove && (
        <button
          type="button"
          className="aud-v2-rule__remove"
          onClick={onRemove}
          disabled={isReadOnly || isSubmitting}
        >
          <span className="material-icons">cancel</span>
        </button>
      )}
    </div>
  );
};

export default RuleRow;
